/**
 * Page Ownership admin page.
 *
 * Lists every Tyashin-owned platform page registered in the backend registry
 * and lets the admin flip each page between:
 *   • Tyashin    — our server-rendered page at that path
 *   • Storefront — the admin's generated R2 bundle serves that path instead
 *
 * Locked utility pages (`/brand-kit.css`, `/tyashin-runtime.js`,
 * `/order-status/lookup`) are shown read-only so admins understand why some
 * registry entries don't have a toggle.
 *
 * Save semantics (single round-trip):
 *   1. User toggles → draft state diverges from `currentMap`.
 *   2. If a toggle flips TO "storefront", we HEAD-probe
 *      `/api/v1/projects/:id/storefront-asset?path=<page>` and render an
 *      inline warning if the R2 bundle has no matching file.
 *   3. "Save changes" PATCHes only the diff. The response includes
 *      `cachePurge: { ok, skipped }`:
 *        • purge.ok            → toast "Changes applied — live now"
 *        • purge.skipped       → toast "Changes saved (CF creds not set)"
 *        • !ok && !skipped     → toast "Changes saved. May take up to 5 min"
 *
 * The backend guarantees eventual consistency via ETag revalidation on
 * `/tyashin-runtime.js`, so a failed edge purge is a UX-speed issue, never a
 * correctness issue.
 *
 * Reference: §5, §12, §14 of `.claude/plans/brand-kit-and-tyashin-pages.md`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Lock, AlertTriangle, type CheckCircle2 } from 'lucide-react';

import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { isVersionConflict, apiErrorMessage } from '@/lib/conflict';
import { RefreshingHint } from '@/components/common/DataState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// ─────────────────────────────────────────────────────────────────────────────
// Types — mirror src/routes/tyashin-pages.ts + src/lib/platform-pages/registry.ts
// ─────────────────────────────────────────────────────────────────────────────

type Ownership = 'tyashin' | 'storefront';

interface AdminPageView {
  id: string;
  title: string;
  description: string;
  methods: readonly ('GET' | 'POST' | 'HEAD')[];
  defaultOwnership: Ownership;
  pathExample: string;
}

interface GetResponse {
  registryVersion: number;
  pagesVersion: number;
  pages: AdminPageView[];
  lockedPages: AdminPageView[];
  currentMap: Record<string, Ownership>;
}

interface PatchResponse {
  tyashinPages: {
    map: Record<string, Ownership>;
    version: number;
  };
  cachePurge: { ok: boolean; skipped: boolean };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract a single `?path=` candidate for the HEAD probe from the registry's
 * `pathExample`. The example is a human-readable string like
 * `/order-status, /order-status/<orderNumber>` — we grab the first comma-
 * separated segment, strip angle-bracketed params, and drop the leading
 * method prefix (`POST /order-status/lookup` → `/order-status/lookup`).
 */
function probePathFor(example: string): string | null {
  // Drop method prefix if any.
  const noMethod = example.replace(/^(GET|POST|PUT|PATCH|DELETE|HEAD)\s+/i, '');
  const first = noMethod.split(',')[0].trim();
  // Strip `/<param>` → they won't match an R2 file literally. Fall back to
  // the static prefix, which is a reasonable "does the bundle cover this
  // path group" probe.
  const staticPrefix =
    first
      .split('/')
      .filter((seg) => !seg.includes('<'))
      .join('/') || '/';
  return staticPrefix.startsWith('/') ? staticPrefix : `/${staticPrefix}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function PageOwnershipPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  // Draft map — only contains keys that differ from `currentMap`. Empty means
  // "nothing to save".
  const [draft, setDraft] = useState<Record<string, Ownership>>({});

  // Per-page "storefront has no file" warnings, keyed by page id. We only
  // populate this lazily when a toggle flips to 'storefront'.
  const [bundleWarnings, setBundleWarnings] = useState<Record<string, boolean>>({});

  const { data, isPending, isFetching, isError, error } = useQuery<GetResponse>({
    queryKey: qk.pageOwnership(projectId),
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}/tyashin-pages`);
      return res.data.data as GetResponse;
    },
    enabled: Boolean(projectId),
  });

  // When the server map changes (reload, save success), clear stale draft.
  useEffect(() => {
    setDraft({});
    setBundleWarnings({});
  }, [data?.pagesVersion]);

  const pages = data?.pages ?? [];
  const lockedPages = data?.lockedPages ?? [];
  const currentMap = data?.currentMap ?? {};

  // Resolve each page's displayed ownership: draft wins, else server map,
  // else the page's registry default.
  const effectiveOwner = useCallback(
    (page: AdminPageView): Ownership => {
      if (draft[page.id]) return draft[page.id];
      return currentMap[page.id] ?? page.defaultOwnership;
    },
    [draft, currentMap]
  );

  /**
   * Trigger the HEAD probe for a page when the admin flips it to
   * 'storefront'. Cached per-page in `bundleWarnings`.
   *
   * Probe errors are swallowed: a failed probe is shown as "no warning"
   * rather than a scary banner. The user still sees the actual R2 404 at
   * runtime if they flip anyway.
   */
  const probeTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const probeBundle = useCallback(
    (page: AdminPageView) => {
      const probePath = probePathFor(page.pathExample);
      if (!probePath) return;
      // Debounce — admins might toggle rapidly while deciding.
      if (probeTimers.current[page.id]) clearTimeout(probeTimers.current[page.id]);
      probeTimers.current[page.id] = setTimeout(async () => {
        try {
          const res = await api.head(
            `/projects/${projectId}/tyashin-pages/storefront-asset?path=${encodeURIComponent(probePath)}`
          );
          // Response 200 → exists, 404 → missing (warn).
          setBundleWarnings((w) => ({ ...w, [page.id]: res.status !== 200 }));
        } catch (err) {
          const status = (err as { response?: { status?: number } }).response?.status;
          if (status === 404) {
            setBundleWarnings((w) => ({ ...w, [page.id]: true }));
          }
          // Other errors: treat as "probe failed, no warning" so we don't
          // falsely scare the admin.
        }
      }, 200);
    },
    [projectId]
  );

  const setOwnership = useCallback(
    (page: AdminPageView, next: Ownership) => {
      // If next matches server map (or default when server silent), the
      // change cancels out — remove from draft.
      const server = currentMap[page.id] ?? page.defaultOwnership;
      setDraft((d) => {
        const copy = { ...d };
        if (next === server) delete copy[page.id];
        else copy[page.id] = next;
        return copy;
      });
      if (next === 'storefront') {
        probeBundle(page);
      } else {
        // Back to tyashin — warning irrelevant.
        setBundleWarnings((w) => {
          const copy = { ...w };
          delete copy[page.id];
          return copy;
        });
      }
    },
    [currentMap, probeBundle]
  );

  const hasChanges = Object.keys(draft).length > 0;

  const saveMutation = useMutation<PatchResponse, Error>({
    mutationFn: async () => {
      const res = await api.patch(`/projects/${projectId}/tyashin-pages`, {
        updates: draft,
        // Optimistic concurrency: the pagesVersion this draft was built
        // against. The server rejects (409) if another admin saved a flip in
        // the meantime, instead of silently merging over their change.
        expectedVersion: data?.pagesVersion ?? 0,
      });
      return res.data.data as PatchResponse;
    },
    onSuccess: (payload) => {
      if (payload.cachePurge.ok) {
        toast.success('Changes applied — live now.');
      } else if (payload.cachePurge.skipped) {
        toast.success(
          'Changes saved. Edge cache purge not configured; changes appear within 5 minutes.'
        );
      } else {
        toast.warning('Changes saved. May take up to 5 minutes to appear on your domain.');
      }
      // Invalidate so GET refetches with the new map+version → effect clears
      // draft.
      queryClient.invalidateQueries({ queryKey: qk.pageOwnership(projectId) });
    },
    onError: (err) => {
      if (isVersionConflict(err)) {
        // Toggle drafts are cheap to redo — refetch the fresh map (the
        // pagesVersion-keyed effect clears the stale draft) and tell the
        // admin to reapply on top of what the other admin changed.
        toast.error(
          'Page ownership was changed by someone else — the list has been refreshed. Please reapply your toggles.'
        );
        queryClient.invalidateQueries({ queryKey: qk.pageOwnership(projectId) });
        return;
      }
      toast.error(apiErrorMessage(err, 'Failed to save page ownership.'));
    },
  });

  const discardChanges = () => {
    setDraft({});
    setBundleWarnings({});
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────────

  // First load only (nothing cached) shows the spinner; background refetches
  // keep the current list on screen behind a quiet "Refreshing…" hint.
  if (isPending) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  // Only surface the hard-error card when there's no cached data to fall back
  // to — a failed background refetch must not blank an already-loaded list.
  if (isError && data === undefined) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Card>
          <CardHeader>
            <CardTitle>Could not load page ownership</CardTitle>
            <CardDescription>
              {apiErrorMessage(error, 'Please refresh and try again.')}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-4xl space-y-6 p-6">
      {isFetching && <RefreshingHint />}
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to={`/projects/${projectId}`}
            className="text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to project
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">Page Ownership</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
            Choose which pages Tyashin serves on your domain, and which ones your own storefront
            bundle owns. The setting takes effect within seconds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={discardChanges} disabled={saveMutation.isPending}>
              Discard
            </Button>
          )}
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save changes
                {hasChanges && (
                  <Badge variant="secondary" className="ml-2">
                    {Object.keys(draft).length}
                  </Badge>
                )}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Editable pages */}
      <div className="space-y-4">
        {pages.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>No toggleable pages</CardTitle>
              <CardDescription>
                Every platform page is currently locked to Tyashin. New pages will appear here as
                they ship.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {pages.map((page) => {
          const owner = effectiveOwner(page);
          const isDraft = Boolean(draft[page.id]);
          const showBundleWarning = owner === 'storefront' && bundleWarnings[page.id] === true;
          return (
            <Card key={page.id} className={isDraft ? 'border-primary' : ''}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="flex items-center gap-2 text-base">
                      {page.title}
                      {isDraft && <Badge variant="secondary">Unsaved</Badge>}
                    </CardTitle>
                    <CardDescription className="mt-1">{page.description}</CardDescription>
                    <p className="text-muted-foreground mt-2 break-all font-mono text-xs">
                      {page.pathExample}
                    </p>
                  </div>
                  <OwnershipToggle
                    value={owner}
                    onChange={(v) => setOwnership(page, v)}
                    disabled={saveMutation.isPending}
                  />
                </div>
              </CardHeader>
              {showBundleWarning && (
                <CardContent className="pt-0">
                  <div className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-900 dark:text-amber-200">
                        No matching file in your bundle
                      </p>
                      <p className="mt-0.5 text-amber-800 dark:text-amber-300/90">
                        Your storefront doesn't ship a page at this path. Regenerate your site with
                        this page included, or switch back to Tyashin's version.
                      </p>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Locked pages (read-only) */}
      {lockedPages.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-muted-foreground mt-8 text-sm font-medium">
            Always served by Tyashin
          </h2>
          <Card>
            <CardContent className="divide-y p-0">
              {lockedPages.map((page) => (
                <div key={page.id} className="flex items-start gap-3 p-4">
                  <Lock className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{page.title}</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">{page.description}</p>
                    <p className="text-muted-foreground mt-1 break-all font-mono text-xs">
                      {page.pathExample}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Version footer for debugging */}
      {data && (
        <p className="text-muted-foreground text-right text-xs">
          Registry v{data.registryVersion} · Ownership v{data.pagesVersion}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OwnershipToggle — segmented control (Tyashin | Storefront)
// ─────────────────────────────────────────────────────────────────────────────

interface OwnershipToggleProps {
  value: Ownership;
  onChange: (next: Ownership) => void;
  disabled?: boolean;
}

function OwnershipToggle({ value, onChange, disabled }: OwnershipToggleProps) {
  const items: Array<{ value: Ownership; label: string; icon: typeof CheckCircle2 | null }> = [
    { value: 'tyashin', label: 'Tyashin', icon: null },
    { value: 'storefront', label: 'Storefront', icon: null },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Page ownership"
      className="bg-background inline-flex shrink-0 rounded-md border p-0.5 text-xs"
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(item.value)}
            className={[
              'rounded px-3 py-1.5 font-medium transition-colors',
              active
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
              disabled ? 'cursor-not-allowed opacity-50' : '',
            ].join(' ')}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
